import { useState } from "react";
import { Button } from "../components/ui/button";
import { ToastContainer } from "../components/ui/toast";
import { Header } from "../components/Header";
import { SEO } from "../components/SEO";
import { FileText, Copy } from "lucide-react";
import logoIcon from "../assets/icone.png";
import { useLanguage } from "../hooks/useLanguage";

// CPF Generator
function generateCPF(): string {
    const randomDigits = (n: number) =>
        Array.from({ length: n }, () => Math.floor(Math.random() * 10));

    const calculateDigit = (digits: number[]): number => {
        const sum = digits.reduce((acc, digit, idx) => acc + digit * (digits.length + 1 - idx), 0);
        const remainder = sum % 11;
        return remainder < 2 ? 0 : 11 - remainder;
    };

    const base = randomDigits(9);
    const digit1 = calculateDigit(base);
    const digit2 = calculateDigit([...base, digit1]);

    const cpf = [...base, digit1, digit2];
    return cpf.join('').replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
}

// CNPJ Generator
function generateCNPJ(): string {
    const randomDigits = (n: number) =>
        Array.from({ length: n }, () => Math.floor(Math.random() * 10));

    const calculateDigit = (digits: number[], weights: number[]): number => {
        const sum = digits.reduce((acc, digit, idx) => acc + digit * (weights[idx] ?? 0), 0);
        const remainder = sum % 11;
        return remainder < 2 ? 0 : 11 - remainder;
    };

    const base = randomDigits(12);
    const weights1 = [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
    const weights2 = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];

    const digit1 = calculateDigit(base, weights1);
    const digit2 = calculateDigit([...base, digit1], weights2);

    const cnpj = [...base, digit1, digit2];
    return cnpj.join('').replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
}

export function DocumentGenerator() {
    const [generatedDocument, setGeneratedDocument] = useState<{ type: 'CPF' | 'CNPJ'; value: string } | null>(null);
    const [toasts, setToasts] = useState<Array<{ id: string; message: string; type?: "success" | "error" | "info" }>>([]);
    const { t } = useLanguage();

    const addToast = (message: string, type: "success" | "error" | "info" = "info") => {
        const id = Date.now().toString();
        setToasts(prev => [...prev, { id, message, type }]);
    };

    const removeToast = (id: string) => {
        setToasts(prev => prev.filter(toast => toast.id !== id));
    };

    const handleGenerateCPF = () => {
        const cpf = generateCPF();
        setGeneratedDocument({ type: 'CPF', value: cpf });
    };

    const handleGenerateCNPJ = () => {
        const cnpj = generateCNPJ();
        setGeneratedDocument({ type: 'CNPJ', value: cnpj });
    };

    const handleCopy = async () => {
        if (!generatedDocument) return;

        try {
            await navigator.clipboard.writeText(generatedDocument.value);
            addToast(t.documentGenerator.result.copySuccess, "success");
        } catch (err) {
            addToast(t.documentGenerator.result.copyError, "error");
        }
    };

    return (
        <div className="min-h-screen bg-white selection:bg-indigo-100 selection:text-indigo-900 font-sans flex flex-col">
            <SEO
                title={t.documentGenerator.hero.title + " | Hub Links"}
                description="Gere CPF e CNPJ válidos gratuitamente para testes. Ferramenta online gratuita para gerar documentos brasileiros válidos."
                keywords="gerar cpf, gerar cnpj, cpf válido, cnpj válido, gerador de cpf, gerador de cnpj, cpf online, cnpj online"
                ogImage="https://hublinks.click/og-documents.jpg"
                ogType="website"
                canonicalUrl="https://hublinks.click/generate-cpf-cnpj"
                structuredData={{
                    "@context": "https://schema.org",
                    "@type": "WebApplication",
                    "name": "Gerador de CPF e CNPJ - Hub Links",
                    "url": "https://hublinks.click/generate-cpf-cnpj",
                    "description": "Gere CPF e CNPJ válidos gratuitamente para testes",
                    "applicationCategory": "UtilitiesApplication",
                    "offers": {
                        "@type": "Offer",
                        "price": "0",
                        "priceCurrency": "BRL"
                    }
                }}
            />
            {/* Hero Section with Gradient Background */}
            <div className="relative overflow-hidden flex-1">
                <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-neutral-900/10 via-white to-white -z-10" />
                <div className="absolute top-0 left-0 w-full h-full bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-20 -z-10 mix-blend-overlay"></div>

                <Header
                    onLoginClick={() => window.location.href = '/login'}
                    onRegisterClick={() => window.location.href = '/register'}
                />

                {/* Hero Content */}
                <main>
                    <div className="max-w-4xl mx-auto text-center space-y-8">
                        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#0DAFFD]/10 border border-[#0DAFFD]/20 text-[#0DAFFD] text-xs font-semibold uppercase tracking-wider shadow-sm animate-in fade-in slide-in-from-bottom-4 duration-700">
                            <FileText className="w-3 h-3" aria-hidden="true" />
                            {t.documentGenerator.hero.badge}
                        </div>

                        <h1 className="text-5xl md:text-7xl font-extrabold tracking-tight text-slate-900 leading-[1.1] animate-in fade-in slide-in-from-bottom-6 duration-700 delay-100">
                            {t.documentGenerator.hero.title} <br className="hidden md:block" />
                            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#0DAFFD] via-[#0FD8FE] to-[#313131]">
                                {t.documentGenerator.hero.titleHighlight}
                            </span>
                        </h1>

                        <p className="text-lg text-slate-600 max-w-2xl mx-auto animate-in fade-in slide-in-from-bottom-8 duration-700 delay-150">
                            {t.documentGenerator.hero.description}
                        </p>

                        <div className="max-w-md mx-auto animate-in fade-in slide-in-from-bottom-8 duration-700 delay-200">
                            <div className="space-y-6">
                                {/* Buttons */}
                                <div className="grid grid-cols-2 gap-4">
                                    <Button
                                        onClick={handleGenerateCPF}
                                        className="h-14 bg-gradient-to-r from-[#0DAFFD] to-[#0FD8FE] hover:from-[#0DAFFD] hover:to-[#0FD8FE] text-white rounded-xl font-semibold transition-all hover:shadow-lg hover:shadow-[#0DAFFD]/30 hover:-translate-y-0.5"
                                    >
                                        {t.documentGenerator.buttons.generateCPF}
                                    </Button>

                                    <Button
                                        onClick={handleGenerateCNPJ}
                                        className="h-14 bg-gradient-to-r from-[#313131] to-[#525252] hover:from-[#313131] hover:to-[#525252] text-white rounded-xl font-semibold transition-all hover:shadow-lg hover:shadow-slate-900/30 hover:-translate-y-0.5"
                                    >
                                        {t.documentGenerator.buttons.generateCNPJ}
                                    </Button>
                                </div>

                                {/* Result Display */}
                                {generatedDocument && (
                                    <div className="space-y-4 animate-in fade-in slide-in-from-bottom-2 duration-500">
                                        <div className="w-full bg-white p-6 rounded-2xl border-2 border-slate-100 shadow-sm">
                                            <div className="space-y-3">
                                                <p className="text-sm font-medium text-slate-600">
                                                    {generatedDocument.type === 'CPF'
                                                        ? t.documentGenerator.result.cpfLabel
                                                        : t.documentGenerator.result.cnpjLabel}
                                                </p>

                                                <div className="flex items-center justify-between gap-4 p-4 bg-slate-50 rounded-xl">
                                                    <p className="text-2xl font-bold text-slate-900 tracking-wider">
                                                        {generatedDocument.value}
                                                    </p>

                                                    <button
                                                        onClick={handleCopy}
                                                        className="flex items-center justify-center w-10 h-10 rounded-lg bg-white border border-slate-200 text-slate-600 hover:text-[#0DAFFD] hover:border-[#0DAFFD] transition-all hover:shadow-md"
                                                        aria-label="Copiar documento"
                                                    >
                                                        <Copy className="w-5 h-5" />
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </main>
            </div>

            {/* Footer */}
            <footer className="bg-white border-t border-slate-100 pt-16 pb-8 mt-auto" role="contentinfo">
                <div className="max-w-7xl mx-auto px-6">
                    <div className="flex flex-col md:flex-row justify-between items-center gap-8">
                        <div className="flex items-center gap-2 font-bold text-lg text-slate-900">
                            <img src={logoIcon} alt={t.footer.logoAlt} className="w-6 h-6 rounded-md" />
                            Hub Links
                        </div>
                        <div className="text-sm text-slate-500">
                            {t.footer.rights}
                        </div>
                        <nav className="flex gap-6" aria-label={t.footer.socialsAriaLabel}>
                            <a href="#" className="text-slate-400 hover:text-indigo-600 transition-colors" aria-label="Twitter">Twitter</a>
                            <a href="#" className="text-slate-400 hover:text-indigo-600 transition-colors" aria-label="Instagram">Instagram</a>
                            <a href="#" className="text-slate-400 hover:text-indigo-600 transition-colors" aria-label="GitHub">GitHub</a>
                        </nav>
                    </div>
                </div>
            </footer>

            <ToastContainer toasts={toasts} onRemove={removeToast} />
        </div>
    );
}
